import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Button, Card, Modal } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  ClockCircleOutlined,
  FacebookOutlined,
  InstagramOutlined,
  LinkedinOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";

import Header from "../../components/common/Layouts/Header";
import Footer from "../../components/common/Layouts/Footer";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import { VALIDATION_MESSAGES } from "../../utils/constants";
import { submitEnquiry, resetSubmitEnquiryStatus } from "../../redux/eventSlice";
import "../../assets/styles/components_styles/supportModule.css";
import { EmailIcon } from "../../assets/CustomIcon";

const SOCIAL_LINKS = [
  { icon: <FacebookOutlined />, href: "https://www.facebook.com/people/Pingvite", label: "Facebook" },
  { icon: <InstagramOutlined />, href: "https://www.instagram.com", label: "Instagram" },
  { icon: <LinkedinOutlined />, href: "https://www.linkedin.com/company/pingvite-private-limited", label: "LinkedIn" },
  { icon: <YoutubeOutlined />, href: "https://www.youtube.com", label: "YouTube" },
];

export default function ContactUsPage() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();

  const enquiryLoading = useSelector((state) => state.event?.submitEnquiryLoading);
  const enquirySuccess = useSelector((state) => state.event?.submitEnquirySuccess);
  const enquiryError = useSelector((state) => state.event?.submitEnquiryError);

  const handleSubmit = (values) => {
    const payload = {
      enquiry_id: Math.floor(100 + Math.random() * 900),
      enquiry_name: values.enquiry_name?.trim(),
      enquiry_email: values.enquiry_email?.trim(),
      enquiry_contact: values.enquiry_contact?.trim(),
      ...(values.enquiry_message?.trim()
        ? { enquiry_message: values.enquiry_message.trim() }
        : {}),
    };

    dispatch(submitEnquiry(payload));
  };

  useEffect(() => {
    if (enquirySuccess) {
      modal.info({
        icon: null,
        title: null,
        content: (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <CheckCircleFilled style={{ color: "#52c41a", fontSize: 22 }} />
            <div>Your enquiry was submitted successfully.</div>
          </div>
        ),
      });
      form.resetFields();
      dispatch(resetSubmitEnquiryStatus());
    }

    if (enquiryError) {
      modal.info({
        icon: null,
        title: null,
        content: (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 22 }} />
            <div>Your enquiry was submitted failed. Please try again.</div>
          </div>
        ),
      });
      dispatch(resetSubmitEnquiryStatus());
    }
  }, [dispatch, enquiryError, enquirySuccess, form, modal]);

  return (
    <>
      <HelmetWrapper
        title="Contact Us - Pingvite"
        description="Reach out to Pingvite for support, partnership, and event related enquiries."
        keywords="Pingvite contact us, event enquiry, support, social links"
      />
      {contextHolder}

      {/* HEADER */}
      <Header isHome={false} />

      {/* CONTENT */}
      <main className="supportModulePageRoot">
        <section className="supportModuleSection">
          <div className="container">
            <div className="supportModuleContent">
              <h1 className="supportModuleTitle">Contact Us</h1>
              <span className="supportModuleTitleUnderline" />

              <Form
                form={form}
                layout="vertical"
                className="supportModuleForm"
                onFinish={handleSubmit}
              >
                <Form.Item
                  name="enquiry_name"
                  rules={[{ required: true, message: VALIDATION_MESSAGES.NAME_REQUIRED }]}
                >
                  <Input
                    size="large"
                    placeholder="Name"
                    prefix={<UserOutlined className="contactUsInputIcon" />}
                  />
                </Form.Item>

                <Form.Item
                  name="enquiry_email"
                  rules={[
                    { required: true, message: VALIDATION_MESSAGES.EMAIL_REQUIRED },
                    { type: "email", message: VALIDATION_MESSAGES.EMAIL_INVALID },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Email"
                    prefix={<MailOutlined className="supportModuleInputIcon" />}
                  />
                </Form.Item>

                <Form.Item
                  name="enquiry_contact"
                  rules={[
                    { required: true, message: VALIDATION_MESSAGES.PHONE_REQUIRED },
                    {
                      pattern: /^[0-9]{8,15}$/,
                      message: VALIDATION_MESSAGES.PHONE_INVALID,
                    },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Phone"
                    prefix={<PhoneOutlined className="supportModuleInputIcon" />}
                  />
                </Form.Item>

                <Form.Item name="enquiry_message">
                  <Input.TextArea rows={5} placeholder="Message" />
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  className="supportModuleSubmitBtn btnGradient"
                  loading={enquiryLoading}
                >
                  Send
                </Button>
              </Form>

              <div className="supportModuleWrap">
                <h2 className="supportModuleTitle">Connect With Us</h2>
                <span className="supportModuleTitleUnderline" />

                <Card className="supportModuleCard" bordered={false}>
                  <div className="supportModuleGrid">
                    <div className="supportModuleCol">
                      <h3 className="supportModuleHeading">Head Office</h3>
                      <p className="supportModuleText">
                        Pingvite Private Limited
                        <br />
                        Tower 6, 401, The Soul, New Town Rajarhat,
                        <br />
                        Kolkata, West Bengal - 700135
                      </p>
                      <p className="supportModuleMeta">
                        <PhoneOutlined />
                        <a href="tel:9007222405">9007222405</a>
                      </p>
                      <p className="supportModuleMeta">
                        <EmailIcon />
                        <a href="mailto:info@pingvite.com">info@pingvite.com</a>
                      </p>
                      <p className="supportModuleMeta">
                        <ClockCircleOutlined />
                        08:00 AM to 08:00 PM
                      </p>
                    </div>

                    <div className="supportModuleCol">
                      <h3 className="supportModuleHeading">Socials</h3>
                      <p className="supportModuleText">
                        Follow us for exciting updates and offers!
                      </p>
                      <div className="supportModuleSocials">
                        {SOCIAL_LINKS.map((item) => (
                          <a
                            key={item.label}
                            href={item.href}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={item.label}
                            className="supportModuleSocialLink"
                          >
                            {item.icon}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <Footer />
    </>
  );
}